import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State } from "@stencil/core";

import { StzhMessageCloseEvent, StzhMessageOpenEvent } from "../../index";

import { StzhMessageLocalizedText } from "./stzh-message.localization";

/**
 * @slot - Slot for description content (use instead of description property)
 * @slot label - Slot for label (use as alternative for property)
 * @slot link - Slot for optional stzh-link element
 */
@Component({
  tag: "stzh-message",
  styleUrl: "stzh-message.scss",
  scoped: true,
})
export class StzhMessage {
  /** Label (slot can also be used as alternative) */
  @Prop() label: string = "";

  /** Description (use instead of default slot) */
  @Prop() description: string = "";

  /** Type */
  /** Do not use page-message property. It will be deprecated with the next major release. */
  @Prop({ reflect: true }) type: "info" | "success" | "warning" | "error" | "page-message" = "info";

  /** Whether to hide close button */
  @Prop() hideClose: boolean = false;

  /** Translation strings. */
  @Prop() localization: StzhMessageLocalizedText;

  /** Message open event */
  @Event() stzhOpen: EventEmitter<StzhMessageOpenEvent>;

  /** Message close event */
  @Event() stzhClose: EventEmitter<StzhMessageCloseEvent>;

  @Element() element: HTMLStzhMessageElement;

  /** Open state */
  @State() open: boolean = true;

  @Method()
  async show() {
    this.open = true;
    this.stzhOpen.emit({
      component: "stzh-message",
    });
  }

  @Method()
  async hide() {
    this.open = false;
    this.stzhClose.emit({
      component: "stzh-message",
    });
  }

  private onCloseButtonClick = () => {
    this.hide();
  };

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "message");
    }
  }

  render() {
    const classes = {
      "stzh-message": true,
      "stzh-message--has-close-hidden": this.hideClose,
      [`stzh-message--type-${this.type}`]: !!this.type,
    };

    return (
      <Host hidden={!this.open}>
        <div class={classes}>
          <div class="stzh-message__content">
            <div class="stzh-message__icon-wrapper">
              <stzh-icon
                class="stzh-message__icon"
                name={
                  ((this.type === "info" || this.type === "page-message") && "info-help-filled") ||
                  (this.type === "success" && "checkmark-circle-filled") ||
                  (this.type === "warning" && "important-warning-filled") ||
                  (this.type === "error" && "warning-filled")
                }
              ></stzh-icon>
            </div>
            <div class="stzh-message__text-wrapper">
              <div class="stzh-message__label">{this.label ? this.label : <slot name="label"></slot>}</div>
              <div class="stzh-message__description">{this.description ? this.description : <slot></slot>}</div>
              <div class="stzh-message__link">
                <slot name="link"></slot>
              </div>
            </div>
          </div>

          {!this.hideClose && (
            <stzh-button
              class="stzh-message__close"
              onClick={this.onCloseButtonClick}
              a11yLabel={this.localization.close}
              variant="tertiary"
              size="small"
              iconOnly={true}
              icon="close-big"
            ></stzh-button>
          )}
        </div>
      </Host>
    );
  }
}
